import { useDispatch, useSelector } from 'react-redux';
import { Message } from '@bwoty-web/ui-kit';
import { useEffect } from 'react';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';

import FadeIn from '../utils/fadeIn';
import GalleryLoader from '../components/shared/loaders/GalleryLoader';
import LoaderHeading from '../components/shared/loaders/LoaderHeading';
import CruiseListHits from '../components/cruiseList/CruiseListHits';
import CruiseCardList from '../components/cruiseList/CruiseCardList';
import CruiseSortPanel from '../components/cruiseList/CruiseSortPanel';
import MessageContainer from '../components/shared/message/MessageContainer';
import CruiseFilterPanel from '../components/cruiseList/filter/CruiseFilterPanel';
import { urlUtils } from '../utils/url';
import { texts, type } from '../assets/texts';
import { globeLoaderImages, loaderImages } from '../assets/imageUrls/cruiseLoaderImages';

import { init } from '../redux/actions/cruiseListActions';
import {
  selectCruises,
  selectIsInitialized,
  selectLabels,
  selectMessage,
  selectSiteSettings,
  selectUserId,
  selectVisibleCruises,
} from '../redux/selectors/cruiseList';

import './cruiseListContainer.scss';

// Takes no props and reads one field per `useSelector` rather than the whole state tree. The
// `isInspiration` conditional that chose between the sorted and merely-filtered list lives in
// `selectVisibleCruises`, which `CruiseCardList` shares.
const CruiseListRootContainer = () => {
  const dispatch = useDispatch();
  const siteSettings = useSelector(selectSiteSettings);
  const userId = useSelector(selectUserId);
  const initialized = useSelector(selectIsInitialized);
  const labels = useSelector(selectLabels) ?? {};
  const message = useSelector(selectMessage);
  const originalCruiseList = useSelector(selectCruises);
  const filteredAndSortedCruises = useSelector(selectVisibleCruises);

  const isGlobetrotter = useIsGlobetrotter();
  useEffect(() => {
    dispatch(init());
  }, [dispatch]);

  const hasSessionId = !!urlUtils.getSessionIdFromUrl();

  if (!initialized) {
    if (hasSessionId) {
      return (
        <LoaderHeading
          heading={texts[siteSettings.siteId].loaderHeadingShort}
          topMargin
        />
      );
    }

    return (
      <GalleryLoader
        siteSettings={siteSettings}
        imageList={isGlobetrotter ? globeLoaderImages : loaderImages}
        heading={texts[siteSettings.siteId].loaderHeading}
        tips={texts[siteSettings.siteId].tips.filter((tip) => tip.type === type.BOTH || tip.type === type.CRUISE)}
      />
    );
  }

  const isZeroHit = originalCruiseList?.length <= 0;
  const filteredToZeroHits = !isZeroHit > 0 && filteredAndSortedCruises?.length === 0;

  return (
    <FadeIn>
      <div className="iw-cruise-list-container">
        <MessageContainer
          message={message}
          user={!isZeroHit ? userId : ''}
          labels={labels}
          style={{ marginBottom: '24px' }}
        />
        {!isZeroHit && (
          <>
            <div className="iw-cruise-list__filter-sort-container">
              <CruiseFilterPanel />
              <CruiseSortPanel />
            </div>
            <CruiseListHits />
            <div className="iw-cruise-list">
              {filteredToZeroHits && (
                <Message
                  header={labels.zeroHitTooltipText}
                  messageType="info"
                />
              )}
              <CruiseCardList />
            </div>
          </>
        )}
      </div>
    </FadeIn>
  );
};

export default CruiseListRootContainer;
